import { useI18nStore, useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { formatDateString } from '@/utils/datetime';
import { formatMoney } from '@/utils/money';
import type { ToolCallMessagePartProps, ToolApprovalResponse } from '@assistant-ui/react';
import {
  AI_WRITE_TOOL_NAMES,
  AiMenuItemsOutputSchema,
  AiReservationListOutputSchema,
  AiRevenueOutputSchema,
  type AiWriteToolName,
} from '@restaurant/api-contracts';
import {
  Block,
  BlockTitle,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Icon,
  List,
  ListItem,
  Preloader,
  f7,
} from 'framework7-react';
import { useState } from 'react';
import { describeWriteTool } from './describe-write-tool';

const WRITE_TOOLS: ReadonlySet<string> = new Set(AI_WRITE_TOOL_NAMES);
const isWriteToolName = (name: string): name is AiWriteToolName => WRITE_TOOLS.has(name);

const useCurrency = () => useRestaurantStore((s) => s.restaurantConfig?.currency ?? 'EUR');

function useToolLabel(toolName: string): string {
  const labels: Record<string, string> = useT().aiAssistant.tools;
  return labels[toolName] ?? toolName;
}

export function ToolRow({ toolName, result, isError, status }: ToolCallMessagePartProps) {
  const t = useT().aiAssistant;
  const label = useToolLabel(toolName);
  const running = status.type === 'running' || (result === undefined && !isError);
  return (
    <div className="ai-tool-row" data-testid={`ai-tool-${toolName}`}>
      {running ? (
        <Preloader size={14} />
      ) : (
        <Icon
          f7={isError ? 'exclamationmark_circle' : 'checkmark_circle'}
          size={16}
          color={isError ? 'red' : 'green'}
        />
      )}
      <span>{label}</span>
      {running && <span className="ai-tool-row-status">{t.toolWorking}</span>}
      {isError && <span className="ai-tool-row-status">{t.toolFailed}</span>}
    </div>
  );
}

export function ReservationListCard(props: ToolCallMessagePartProps) {
  const t = useT().aiAssistant.widgets;
  const statusLabels: Record<string, string> = t.status;
  const parsed = AiReservationListOutputSchema.safeParse(props.result);
  if (!parsed.success) return <ToolRow {...props} />;
  const data = parsed.data;
  return (
    <Card outline className="ai-tool-card" data-testid="ai-widget-reservations">
      <CardHeader>
        <div>
          <div className="ai-tool-card-title">
            {t.reservationsTitle(formatDateString(data.date))}
          </div>
          <div className="ai-tool-card-subtitle">
            {t.reservationsSummary(data.totalReservations, data.totalGuests)}
          </div>
        </div>
      </CardHeader>
      <CardContent padding={false}>
        {data.reservations.length === 0 ? (
          <Block className="ai-tool-card-empty">{t.noReservations}</Block>
        ) : (
          <List dividersIos>
            {data.reservations.map((r) => (
              <ListItem
                key={r.id}
                title={r.guestName}
                after={r.time}
                footer={`${t.partySize(r.partySize)} · ${statusLabels[r.status] ?? r.status}`}
              />
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}

export function RevenueCard(props: ToolCallMessagePartProps) {
  const t = useT().aiAssistant.widgets;
  const locale = useI18nStore((s) => s.locale);
  const parsed = AiRevenueOutputSchema.safeParse(props.result);
  if (!parsed.success) return <ToolRow {...props} />;
  const d = parsed.data;
  const money = (minor: number) => formatMoney(minor, d.currency, locale);
  const range =
    d.from === d.to
      ? formatDateString(d.from)
      : `${formatDateString(d.from)} – ${formatDateString(d.to)}`;
  return (
    <Card outline className="ai-tool-card" data-testid="ai-widget-revenue">
      <CardHeader>
        <div>
          <div className="ai-tool-card-title">{t.revenueTitle}</div>
          <div className="ai-tool-card-subtitle">{range}</div>
        </div>
      </CardHeader>
      <CardContent padding={false}>
        <List dividersIos>
          <ListItem title={t.netSales} after={money(d.netSalesCents)} />
          <ListItem title={t.grossSales} after={money(d.grossSalesCents)} />
          <ListItem title={t.orders} after={String(d.orderCount)} />
          <ListItem title={t.online} after={money(d.onlineNetSalesCents)} />
          <ListItem title={t.dineIn} after={money(d.dineInNetSalesCents)} />
          <ListItem title={t.covers} after={String(d.dineInCovers)} />
          <ListItem title={t.tips} after={money(d.tipsCents)} />
        </List>
      </CardContent>
    </Card>
  );
}

export function MenuItemsCard(props: ToolCallMessagePartProps) {
  const t = useT().aiAssistant.widgets;
  const locale = useI18nStore((s) => s.locale);
  const currency = useCurrency();
  const parsed = AiMenuItemsOutputSchema.safeParse(props.result);
  if (!parsed.success) return <ToolRow {...props} />;
  const soldOut = parsed.data.items.filter((i) => !i.inStock);
  const shown = soldOut.length > 0 ? soldOut : parsed.data.items.slice(0, 8);
  return (
    <Card outline className="ai-tool-card" data-testid="ai-widget-menu-items">
      <CardHeader>
        <div>
          <div className="ai-tool-card-title">{t.availabilityTitle}</div>
          <div className="ai-tool-card-subtitle">
            {t.availabilitySummary(parsed.data.items.length, soldOut.length)}
          </div>
        </div>
      </CardHeader>
      <CardContent padding={false}>
        <List dividersIos>
          {shown.map((item) => (
            <ListItem
              key={item.productId}
              title={item.name}
              footer={item.category}
              after={
                !item.isActive
                  ? t.inactive
                  : item.inStock
                    ? formatMoney(item.price, currency, locale)
                    : t.soldOut
              }
            />
          ))}
        </List>
      </CardContent>
    </Card>
  );
}

type ApprovalPhase = 'preparing' | 'pending' | 'denied' | 'running' | 'done' | 'failed';

function approvalPhase(props: ToolCallMessagePartProps): ApprovalPhase {
  const { approval, result, isError } = props;
  if (isError) return 'failed';
  if (approval?.approved === false || approval?.resolution) return 'denied';
  if (!approval) return result === undefined ? 'preparing' : 'done';
  if (approval.approved === undefined) return 'pending';
  return result === undefined ? 'running' : 'done';
}

export function ApprovalCard(props: ToolCallMessagePartProps) {
  const t = useT().aiAssistant;
  const locale = useI18nStore((s) => s.locale);
  const currency = useCurrency();
  const label = useToolLabel(props.toolName);
  const [answering, setAnswering] = useState(false);
  const phase = approvalPhase(props);
  const lines = isWriteToolName(props.toolName)
    ? describeWriteTool(props.toolName, props.args, t, currency, locale)
    : [];

  const answer = async (response: ToolApprovalResponse) => {
    if (answering) return;
    setAnswering(true);
    try {
      await props.respondToApproval(response);
    } catch {
      f7.toast.show({ text: t.approvalFailed, closeTimeout: 3000, position: 'bottom' });
      setAnswering(false);
    }
  };

  const statusText: Record<ApprovalPhase, string> = {
    preparing: t.toolWorking,
    pending: t.approvalPending,
    denied: t.approvalDenied,
    running: t.approvalRunning,
    done: t.approvalDone,
    failed: t.approvalFailed,
  };

  return (
    <Card
      outline
      className={`ai-tool-card ai-approval-card ai-approval-${phase}`}
      data-testid={`ai-approval-${props.toolCallId}`}
    >
      <CardHeader>
        <div>
          <div className="ai-tool-card-title">{label}</div>
          <div className="ai-tool-card-subtitle">{statusText[phase]}</div>
        </div>
        {(phase === 'running' || phase === 'preparing') && <Preloader size={18} />}
      </CardHeader>
      {lines.length > 0 && (
        <CardContent padding={false}>
          <List dividersIos>
            {lines.map((line, index) => (
              <ListItem key={`${index}-${line}`} title={line} />
            ))}
          </List>
        </CardContent>
      )}
      {phase === 'failed' && typeof props.result === 'string' && (
        <BlockTitle className="ai-approval-error">{props.result}</BlockTitle>
      )}
      {phase === 'pending' && (
        <CardFooter>
          <Button
            outline
            color="red"
            disabled={answering}
            data-testid={`ai-approval-deny-${props.toolCallId}`}
            onClick={() => void answer({ approved: false })}
          >
            {t.deny}
          </Button>
          <Button
            fill
            disabled={answering}
            data-testid={`ai-approval-approve-${props.toolCallId}`}
            onClick={() => void answer({ approved: true })}
          >
            {t.approve}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
